/**
 * DataExplorer — cross-project flat-table view of every task.
 *
 * Iter43. Renders one row per task with every canonical column PLUS every
 * custom field discovered across the loaded syncs, all in a single virtualised
 * grid. Filter dropdowns per column (multi-select + text search, iter40),
 * status bar with counts + numeric column totals, CSV export.
 *
 * Scope pickers at the top:
 *   • Share link — restrict to what a given link exposes.
 *   • Exclude share-linked syncs — isolate internal-only data.
 *   • Project(s) — narrow by project(s).
 *
 * The page fetches ALL matching rows in one request (up to 50k). That's
 * fine for our tenant sizes today; when we grow past that we can add
 * cursor-based pagination without breaking the URL surface.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FixedSizeList } from "react-window";
import { Download, Loader2, Search } from "lucide-react";

import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import {
  getDataTasks,
  getDataTasksCsvUrl,
  listDataProjects,
  listDataShareLinks,
} from "../lib/api";

// ─── column meta ───────────────────────────────────────────────────────────

// Canonical column ordering + display labels. Any key present in the rows
// but NOT in this list gets appended alphabetically (typical for cf_*).
const CANONICAL_COLUMNS = [
  ["project_name", "Projeto"],
  ["sync_version", "v"],
  ["sync_name", "Versão"],
  ["sync_is_baseline", "Baseline"],
  ["task_id", "ID"],
  ["name", "Tarefa"],
  ["wbs", "WBS"],
  ["outline_level", "Nível"],
  ["is_summary", "Sumário"],
  ["is_milestone", "Marco"],
  ["is_critical", "Crítico"],
  ["status", "Status"],
  ["start", "Início"],
  ["finish", "Término"],
  ["duration_days", "Dur (d)"],
  ["percent_complete", "%"],
  ["baseline_start", "Baseline Start"],
  ["baseline_finish", "Baseline Finish"],
  ["baseline_duration", "Baseline Dur"],
  ["actual_start", "Actual Start"],
  ["actual_finish", "Actual Finish"],
  ["actual_duration", "Actual Dur"],
  ["early_start", "Early Start"],
  ["early_finish", "Early Finish"],
  ["late_start", "Late Start"],
  ["late_finish", "Late Finish"],
  ["total_slack", "Total Slack"],
  ["free_slack", "Free Slack"],
  ["deadline", "Deadline"],
  ["cost", "Cost"],
  ["actual_cost", "Actual Cost"],
  ["work_days", "Work"],
  ["actual_work_days", "Actual Work"],
  ["resource_names", "Recursos"],
  ["location", "Location"],
  ["notes", "Notes"],
  ["predecessors_count", "Preds"],
  ["project_id", "Project ID"],
  ["sync_id", "Sync ID"],
  ["task_uid", "Task UID"],
  ["parent_uid", "Parent UID"],
];

// Column types drive filter widget + numeric aggregation.
const COL_TYPES = {
  duration_days: "number", percent_complete: "number", outline_level: "number",
  baseline_duration: "number", actual_duration: "number", total_slack: "number",
  free_slack: "number", cost: "number", actual_cost: "number",
  work_days: "number", actual_work_days: "number", predecessors_count: "number",
  sync_version: "number",
  start: "date", finish: "date", baseline_start: "date", baseline_finish: "date",
  actual_start: "date", actual_finish: "date", early_start: "date",
  early_finish: "date", late_start: "date", late_finish: "date", deadline: "date",
  constraint_date: "date", sync_created_at: "date",
  is_summary: "boolean", is_milestone: "boolean", is_critical: "boolean",
  sync_is_baseline: "boolean",
};

const NUMERIC_TYPES = new Set(["number"]);

const COL_WIDTHS = {
  project_name: 180, sync_name: 160, name: 300, wbs: 100, task_id: 70,
  is_summary: 80, is_milestone: 70, is_critical: 70, sync_is_baseline: 80,
  start: 100, finish: 100, resource_names: 180, notes: 220,
};
const DEFAULT_WIDTH = 120;

const ROW_HEIGHT = 30;

// ─── page ──────────────────────────────────────────────────────────────────

export default function DataExplorer() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;

  const [shareLinks, setShareLinks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [shareLinkFilter, setShareLinkFilter] = useState(""); // "" | "__EXCLUDE__" | "<id>"
  const [projectFilter, setProjectFilter] = useState(""); // "" | "<id>"

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [totals, setTotals] = useState({ rows: 0, syncs: 0, projects: 0 });
  const [loading, setLoading] = useState(true);
  const [colFilters, setColFilters] = useState({}); // { col: string | string[] }

  // Column selection for numeric totals.
  const [selectedCols, setSelectedCols] = useState(() => new Set());

  useEffect(() => {
    Promise.all([listDataShareLinks(), listDataProjects()])
      .then(([sl, ps]) => { setShareLinks(sl.share_links || []); setProjects(ps.projects || []); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (shareLinkFilter === "__EXCLUDE__") params.exclude_share_links = true;
    else if (shareLinkFilter) params.share_link_id = shareLinkFilter;
    if (projectFilter) params.project_ids = projectFilter;
    getDataTasks(params)
      .then((res) => {
        if (cancelled) return;
        setRows(res.rows || []);
        setColumns(res.columns || []);
        setTotals(res.totals || { rows: 0, syncs: 0, projects: 0 });
        setColFilters({}); // reset per-column filters on scope change
      })
      .catch((err) => toast.error(err.response?.data?.detail || err.message))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isAdmin, shareLinkFilter, projectFilter]);

  // Column ordering: canonical first, then any extras (cf_*) alphabetically.
  const orderedColumns = useMemo(() => {
    const known = new Map(CANONICAL_COLUMNS);
    const inCanonical = new Set(CANONICAL_COLUMNS.map(([k]) => k));
    const canonicalPresent = CANONICAL_COLUMNS.filter(([k]) => columns.includes(k));
    const extras = columns.filter((c) => !inCanonical.has(c)).sort();
    return [
      ...canonicalPresent.map(([k, lbl]) => ({ key: k, label: lbl, type: COL_TYPES[k] || "text" })),
      ...extras.map((k) => ({
        key: k,
        label: k.startsWith("cf_") ? k.slice(3).replace(/_/g, " ") : k,
        type: COL_TYPES[k] || "text",
      })),
    ];
  }, [columns]);

  // Distinct values per column, computed from ALL rows (not filtered ones)
  // so the dropdown shows every option even after selecting some.
  const distinctByCol = useMemo(() => {
    const out = {};
    for (const col of orderedColumns) {
      const set = new Set();
      for (const r of rows) {
        const v = r[col.key];
        if (v == null || v === "") continue;
        set.add(String(v));
        if (set.size > 500) break;
      }
      out[col.key] = Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }
    return out;
  }, [rows, orderedColumns]);

  const filteredRows = useMemo(() => {
    const active = Object.entries(colFilters).filter(([, v]) => (Array.isArray(v) ? v.length : (v || "").toString().trim()));
    if (!active.length) return rows;
    return rows.filter((r) => active.every(([col, val]) => matchCell(r[col], val)));
  }, [rows, colFilters]);

  const totalsByCol = useMemo(() => {
    // Sum numeric columns across FILTERED rows.
    const out = {};
    for (const col of orderedColumns) {
      if (!NUMERIC_TYPES.has(col.type)) continue;
      if (!selectedCols.has(col.key)) continue;
      let sum = 0;
      let n = 0;
      for (const r of filteredRows) {
        const v = r[col.key];
        const num = Number(v);
        if (Number.isFinite(num)) { sum += num; n += 1; }
      }
      out[col.key] = { sum, count: n, avg: n ? sum / n : 0 };
    }
    return out;
  }, [filteredRows, orderedColumns, selectedCols]);

  const setFilter = (col, val) => setColFilters((p) => ({ ...p, [col]: val }));
  const clearFilters = () => setColFilters({});
  const toggleColSelected = (key) => setSelectedCols((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const onExport = () => {
    const params = {};
    if (shareLinkFilter === "__EXCLUDE__") params.exclude_share_links = "true";
    else if (shareLinkFilter) params.share_link_id = shareLinkFilter;
    if (projectFilter) params.project_ids = projectFilter;
    const url = getDataTasksCsvUrl(params);
    // The endpoint is auth-gated by cookie/JWT; open as a normal navigation
    // so the browser sends credentials.
    window.open(url, "_blank", "noopener");
  };

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto py-16 px-6 text-center">
        <h1 className="font-heading text-2xl font-bold">{t("data_page_title")}</h1>
        <p className="text-zinc-500 mt-2">{t("data_admin_only")}</p>
      </div>
    );
  }

  return (
    <div className="p-6 h-full flex flex-col" data-testid="data-explorer">
      <header className="flex items-end justify-between gap-3 mb-4">
        <div>
          <div className="cs-label">database</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-zinc-950 mt-2" data-testid="data-title">{t("data_page_title")}</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">{t("data_page_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onExport}
            disabled={loading}
            className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-800 px-3 py-2 rounded-md text-xs font-semibold hover:bg-zinc-100 disabled:opacity-60"
            data-testid="data-export-csv-btn"
          >
            <Download size={13} /> {t("data_export_csv")}
          </button>
        </div>
      </header>

      {/* Scope pickers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="bg-white border border-zinc-200 rounded-md p-3">
          <label className="cs-label !text-[10px]">{t("data_filter_share_link")}</label>
          <select
            value={shareLinkFilter}
            onChange={(e) => setShareLinkFilter(e.target.value)}
            className="mt-1.5 w-full px-2 py-1.5 border border-zinc-300 rounded text-xs"
            data-testid="data-share-link-filter"
          >
            <option value="">{t("data_filter_all_data")}</option>
            <option value="__EXCLUDE__">{t("data_filter_exclude_share_links")}</option>
            <option disabled>──────────</option>
            {shareLinks.map((sl) => (
              <option key={sl.id} value={sl.id}>{sl.name}</option>
            ))}
          </select>
        </div>
        <div className="bg-white border border-zinc-200 rounded-md p-3">
          <label className="cs-label !text-[10px]">{t("data_filter_project")}</label>
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className="mt-1.5 w-full px-2 py-1.5 border border-zinc-300 rounded text-xs"
            data-testid="data-project-filter"
          >
            <option value="">{t("data_filter_all_projects")}</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden flex-1 flex flex-col min-h-0" data-testid="data-table-card">
        <TableHeader
          columns={orderedColumns}
          colFilters={colFilters}
          distinctByCol={distinctByCol}
          onFilterChange={setFilter}
          selectedCols={selectedCols}
          onToggleSelected={toggleColSelected}
        />
        <TableBody
          columns={orderedColumns}
          rows={filteredRows}
          loading={loading}
        />
        <StatusBar
          filteredCount={filteredRows.length}
          totalCount={totals.rows}
          syncCount={totals.syncs}
          projectCount={totals.projects}
          columnTotals={totalsByCol}
          columns={orderedColumns}
          onClearFilters={clearFilters}
        />
      </div>
    </div>
  );
}

// ─── table sub-components ──────────────────────────────────────────────────

function TableHeader({ columns, colFilters, distinctByCol, onFilterChange, selectedCols, onToggleSelected }) {
  const { t } = useI18n();
  return (
    <div className="border-b border-zinc-200 bg-zinc-50 sticky top-0 z-10">
      <div className="flex">
        {columns.map((col) => {
          const w = COL_WIDTHS[col.key] || DEFAULT_WIDTH;
          const isNumeric = NUMERIC_TYPES.has(col.type);
          const isSelected = selectedCols.has(col.key);
          return (
            <div
              key={col.key}
              className={`shrink-0 border-r border-zinc-200 ${isSelected ? "bg-cs-accent/5" : ""}`}
              style={{ width: w }}
            >
              <button
                onClick={() => isNumeric && onToggleSelected(col.key)}
                disabled={!isNumeric}
                className={`w-full px-2 py-1.5 text-[10px] font-mono uppercase tracking-wider text-left truncate ${isNumeric ? "cursor-pointer hover:bg-zinc-100" : ""} ${isSelected ? "text-cs-accent font-bold" : "text-zinc-600"}`}
                title={isNumeric ? t("data_toggle_sum") : col.label}
                data-testid={`data-col-header-${col.key}`}
              >
                {col.label}
                {isSelected && " Σ"}
              </button>
              <ColumnFilter
                col={col}
                value={colFilters[col.key]}
                onChange={(v) => onFilterChange(col.key, v)}
                distinct={distinctByCol[col.key] || []}
                width={w}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TableBody({ columns, rows, loading }) {
  const { t } = useI18n();
  const bodyRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 600 });

  useEffect(() => {
    if (!bodyRef.current) return;
    const el = bodyRef.current;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const totalWidth = columns.reduce((a, c) => a + (COL_WIDTHS[c.key] || DEFAULT_WIDTH), 0);

  if (loading) {
    return (
      <div ref={bodyRef} className="flex-1 flex items-center justify-center text-zinc-400" data-testid="data-loading">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div ref={bodyRef} className="flex-1 flex items-center justify-center text-zinc-400 text-sm" data-testid="data-empty">
        {t("no_data")}
      </div>
    );
  }
  return (
    <div ref={bodyRef} className="flex-1 overflow-auto" data-testid="data-body">
      <div style={{ width: Math.max(totalWidth, size.w) }}>
        <FixedSizeList
          height={size.h}
          width={Math.max(totalWidth, size.w)}
          itemCount={rows.length}
          itemSize={ROW_HEIGHT}
          overscanCount={12}
        >
          {({ index, style }) => (
            <Row style={style} row={rows[index]} columns={columns} zebra={index % 2 === 1} />
          )}
        </FixedSizeList>
      </div>
    </div>
  );
}

function Row({ style, row, columns, zebra }) {
  return (
    <div style={style} className={`flex ${zebra ? "bg-zinc-50" : "bg-white"} border-b border-zinc-100 hover:bg-blue-50 text-xs`}>
      {columns.map((col) => {
        const w = COL_WIDTHS[col.key] || DEFAULT_WIDTH;
        return (
          <div
            key={col.key}
            className="shrink-0 border-r border-zinc-100 px-2 py-1 truncate font-mono"
            style={{ width: w }}
            title={String(row[col.key] ?? "")}
          >
            {renderCell(row[col.key], col.type)}
          </div>
        );
      })}
    </div>
  );
}

function renderCell(v, type) {
  if (v == null || v === "") return "";
  if (type === "date") return String(v).slice(0, 10);
  if (type === "boolean") return v === true ? "✓" : (v === false ? "" : String(v));
  if (type === "number" && typeof v === "number") return v.toFixed(v % 1 === 0 ? 0 : 2);
  return String(v);
}

function StatusBar({ filteredCount, totalCount, syncCount, projectCount, columnTotals, columns, onClearFilters }) {
  const { t } = useI18n();
  const totalKeys = Object.keys(columnTotals);
  return (
    <div className="border-t border-zinc-200 bg-zinc-50 px-3 py-2 flex items-center justify-between gap-4 text-[11px] font-mono flex-wrap" data-testid="data-status-bar">
      <div className="flex items-center gap-3 text-zinc-700">
        <span data-testid="data-row-count">
          <b className="text-zinc-950">{filteredCount.toLocaleString()}</b>
          {filteredCount !== totalCount && <> / {totalCount.toLocaleString()}</>} {t("tasks")}
        </span>
        <span className="text-zinc-400">·</span>
        <span>{syncCount} {t("data_syncs_count")}</span>
        <span className="text-zinc-400">·</span>
        <span>{projectCount} {t("data_projects_count")}</span>
        {filteredCount !== totalCount && (
          <>
            <span className="text-zinc-400">·</span>
            <button onClick={onClearFilters} className="text-cs-blueprint hover:underline" data-testid="data-clear-filters">
              {t("clear_filters")}
            </button>
          </>
        )}
      </div>
      {totalKeys.length > 0 && (
        <div className="flex items-center gap-3 text-zinc-700 flex-wrap">
          {totalKeys.map((k) => {
            const col = columns.find((c) => c.key === k);
            const info = columnTotals[k];
            return (
              <span key={k} className="inline-flex items-center gap-1 bg-cs-accent/10 text-cs-accent px-2 py-0.5 rounded" data-testid={`data-total-${k}`}>
                <span className="uppercase tracking-wider text-[9px]">{col?.label || k}</span>
                <span className="font-bold">Σ {info.sum.toFixed(info.sum % 1 === 0 ? 0 : 2)}</span>
                <span className="text-[9px] opacity-70">avg {info.avg.toFixed(2)}</span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── filter widget ─────────────────────────────────────────────────────────

const SEARCHABLE_KEYS = new Set(["name", "task_id", "wbs", "notes", "resource_names", "location"]);

function ColumnFilter({ col, value, onChange, distinct, width }) {
  const { t } = useI18n();
  // Text filter for searchable columns (substring match)
  if (SEARCHABLE_KEYS.has(col.key)) {
    return (
      <div className="px-1 pb-1" style={{ width }}>
        <div className="relative">
          <Search size={10} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full pl-5 pr-1 py-0.5 text-[10px] border border-zinc-200 rounded font-mono bg-white focus:border-cs-accent outline-none"
            placeholder={t("filter_search_placeholder")}
            data-testid={`data-filter-${col.key}`}
          />
        </div>
      </div>
    );
  }
  return <MultiSelectDropdown col={col} value={value} onChange={onChange} distinct={distinct} width={width} />;
}

function MultiSelectDropdown({ col, value, onChange, distinct, width }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  const selected = useMemo(() => {
    if (Array.isArray(value)) return new Set(value.map(String));
    if (value == null || value === "") return new Set();
    return new Set([String(value)]);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const outside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", outside); document.removeEventListener("keydown", esc); };
  }, [open]);

  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(Array.from(next));
  };
  const clear = () => onChange([]);
  const selectAll = () => onChange(distinct.map(String));

  const filtered = q ? distinct.filter((v) => String(v).toLowerCase().includes(q.toLowerCase())) : distinct;
  const label = selected.size === 0
    ? t("filter_all")
    : selected.size === 1 ? Array.from(selected)[0]
      : `${selected.size} ${t("filter_selected_short")}`;

  return (
    <div ref={ref} className="relative px-1 pb-1" style={{ width }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full px-1.5 py-0.5 text-[10px] border rounded font-mono bg-white outline-none flex items-center justify-between gap-1 ${selected.size ? "border-cs-accent text-cs-accent" : "border-zinc-200 text-zinc-700"}`}
        data-testid={`data-filter-${col.key}`}
      >
        <span className="truncate">{label}</span>
        <span className="opacity-50">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 left-1 top-full mt-1 w-56 bg-white border border-zinc-300 rounded-md shadow-lg p-2">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("filter_search_placeholder")}
            className="w-full px-2 py-1 mb-2 text-[11px] border border-zinc-200 rounded font-mono outline-none focus:border-cs-accent"
            autoFocus
          />
          <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-1.5">
            <button onClick={selectAll} className="hover:text-cs-blueprint">{t("filter_select_all")}</button>
            <button onClick={clear} className="hover:text-red-600">{t("filter_clear")}</button>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.slice(0, 500).map((v) => {
              const sv = String(v);
              const checked = selected.has(sv);
              return (
                <label key={sv} className="flex items-center gap-2 px-1 py-0.5 hover:bg-zinc-50 cursor-pointer text-xs">
                  <input type="checkbox" checked={checked} onChange={() => toggle(sv)} className="rounded text-cs-accent focus:ring-cs-accent shrink-0" />
                  <span className="truncate font-mono">{col.type === "boolean" ? (sv === "true" ? "✓" : "—") : (col.type === "date" ? sv.slice(0, 10) : sv)}</span>
                </label>
              );
            })}
            {filtered.length === 0 && <div className="text-center text-[10px] text-zinc-400 py-3">{t("no_match")}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── filter matching (client-side) ─────────────────────────────────────────

function matchCell(cellValue, filterValue) {
  if (cellValue == null || cellValue === "") return false;
  // Multi-select: exact match against any of the selected values (dates
  // are compared as YYYY-MM-DD).
  if (Array.isArray(filterValue)) {
    if (!filterValue.length) return true;
    const norm = (v) => String(v).length > 10 && /^\d{4}-\d{2}-\d{2}T/.test(String(v)) ? String(v).slice(0, 10) : String(v);
    const cv = norm(cellValue).toLowerCase();
    return filterValue.some((f) => norm(f).toLowerCase() === cv);
  }
  // Text search: substring match (case-insensitive).
  const f = filterValue.toString().trim().toLowerCase();
  if (!f) return true;
  return String(cellValue).toLowerCase().includes(f);
}
